import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { useAdminScopeTerms } from '../../lib/adminScopeTerms';

/**
 * Shared "Department Head + Key Responsibilities" block shown right after
 * each department's top command-center board. Two-card layout:
 *   • left  — Department Head (name · reporting line · department-code pill)
 *   • right — Key Responsibilities (bulleted, terminology-aware)
 * Rendered identically across every department command center.
 */
export const DepartmentDetails = ({ dept, prefix, responsibilities, accent = '#C9A962' }) => {
  const terms = useAdminScopeTerms();
  const resp = responsibilities || dept?.responsibilities || [];
  const reportsLine = dept?.key === 'executive'
    ? 'Reports to: Board of Directors'
    : 'Reporting line: Executive Office';
  return (
    <div className="grid lg:grid-cols-3 gap-4 min-w-0" data-testid={`${prefix}-details`}>
      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-1 min-w-0" data-testid={`${prefix}-head-card`}>
        <p className="text-[10px] uppercase mb-2" style={{ color: accent, letterSpacing: '0.2em' }}>
          Department Head
        </p>
        <p
          className="text-base font-bold text-white break-words"
          style={{ fontFamily: 'Barlow Condensed, sans-serif' }}
          data-testid={`${prefix}-position`}
        >
          {dept?.head}
        </p>
        <p className="text-xs text-zinc-400 mt-1">{reportsLine}</p>
        {dept?.code && (
          <>
            <div className="h-px w-full my-4" style={{ backgroundColor: 'rgba(201,169,98,0.25)' }} />
            <p className="text-[10px] uppercase mb-2 text-zinc-500" style={{ letterSpacing: '0.2em' }}>
              Department Code
            </p>
            <Badge
              className="text-xs"
              style={{ backgroundColor: `${accent}22`, color: accent, border: `1px solid ${accent}66` }}
              data-testid={`${prefix}-code`}
            >
              {dept.code}
            </Badge>
          </>
        )}
      </Card>

      <Card className="bg-zinc-900 border-zinc-800 p-5 lg:col-span-2 min-w-0" data-testid={`${prefix}-responsibilities`}>
        <p className="text-[10px] uppercase mb-3" style={{ color: accent, letterSpacing: '0.2em' }}>
          Key Responsibilities
        </p>
        <ul className="space-y-2">
          {resp.map((r, i) => (
            <li
              key={i}
              className="flex items-start gap-3 text-sm text-zinc-200"
              data-testid={`${prefix}-responsibility-${i}`}
            >
              <span
                aria-hidden="true"
                className="mt-1.5 inline-block rounded-full flex-shrink-0"
                style={{ width: '6px', height: '6px', backgroundColor: accent }}
              />
              <span className="break-words">{terms.text(r)}</span>
            </li>
          ))}
        </ul>
      </Card>
    </div>
  );
};
